import React, { FC, useCallback, useEffect, useState } from "react";
import {
  Navigate,
  useLocation,
  useNavigate,
  useParams,
} from "react-router-dom";
import Box from "../../Unknown/Box";
import CircularProgress from "../../Unknown/CircularProgress";
import Typography from "../../Unknown/Typography";
import { useAppContext } from "../../Unknown/AppContext";
import usePatientData from "../PatientInformationDrawerContainer/usePatientData";
import MergePatientsContainer from "../MergePatients";
import PatientInformationDrawerContainer from "../PatientInformationDrawerContainer";
import useMergeScreenPatientDrawer from "../PatientInformationDrawerContainer/useMergeScreenPatientDrawer";
import getPatientForMerge from "../MergePatients/getPatientForMerge";
import { InitialPatient } from "../MergePatients/types";

const MERGE_ALLOWED_ROLES = ["regional_admin", "super_admin"] as const;

// Route-level screen for ":role/.../patient/:patientMrn/merge" — mirrors
// MergeCustomersScreen exactly: full page (shared app header via
// AuthenticatedLayout), not a modal.
const MergePatientsScreen: FC = () => {
  const { patientMrn } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { databaseUserData } = useAppContext();

  const parentPath = location.pathname.replace(/\/merge\/?$/, "");

  const {
    isLoading: isPatientLoading,
    error,
    data: patient,
  } = usePatientData(patientMrn);

  const [initialPatient, setInitialPatient] = useState<InitialPatient | null>(
    null,
  );
  const [isMergeDataLoading, setIsMergeDataLoading] = useState(false);

  useEffect(() => {
    if (!patient) return;
    setIsMergeDataLoading(true);
    getPatientForMerge(patient.id)
      .then(setInitialPatient)
      .finally(() => setIsMergeDataLoading(false));
  }, [patient]);

  const canMerge = MERGE_ALLOWED_ROLES.some((role) =>
    databaseUserData.permissions.includes(role),
  );

  // AC24 — Cancel returns to wherever the merge screen was opened from.
  const handleDone = useCallback(() => {
    navigate(-1);
  }, [navigate]);

  // Mirrors MergeCustomersScreen's AC33/34 handling — the selected patient
  // may not be the one whose drawer opened this screen. The route is keyed
  // by MRN, not id.
  const handleViewPatient = useCallback(
    (selectedPatientMrn: string) => {
      const viewPatientPath = parentPath.replace(
        /\/patient\/[^/]+$/,
        `/patient/${selectedPatientMrn}`,
      );
      navigate(viewPatientPath, { replace: true });
    },
    [navigate, parentPath],
  );

  // Patient links in the comparison headers open the drawer on top of this
  // screen rather than navigating to the "patient/:mrn" route, which would
  // unmount the screen and drop the merge in progress.
  const { isPatientDrawerOpen, openPatientDrawer, patientDrawerProps } =
    useMergeScreenPatientDrawer();

  const isLoading = isPatientLoading || isMergeDataLoading;

  if (!canMerge) {
    return <Navigate replace to={parentPath} />;
  }

  if (isLoading || !initialPatient) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" p={6}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" p={6}>
        <Typography variant="body1" color="error">
          {error}
        </Typography>
      </Box>
    );
  }

  return (
    <>
      <MergePatientsContainer
        initialPatient={initialPatient}
        onDone={handleDone}
        onViewPatient={handleViewPatient}
        onPatientClick={openPatientDrawer}
      />
      {isPatientDrawerOpen && (
        <PatientInformationDrawerContainer {...patientDrawerProps} />
      )}
    </>
  );
};

export default MergePatientsScreen;
